import { Alert, Box, CircularProgress, Container } from '@mui/material';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import OngoingAssessmentReplayPresentation from '../components/assessment/OngoingAssessmentReplayPresentation';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { Securables } from '../Enums';
import {
  CAPTURE_REPLAY_ORGANIZATION_IDS,
  isNorthstarCaptureReplayOrganization,
} from '../Environment';
import OngoingAssessmentReplayService, {
  expectedOngoingAssessmentReplayFingerprint,
  OngoingAssessmentReplay,
  ongoingAssessmentReplayFingerprint,
} from '../services/OngoingAssessmentReplayService';

export const isCaptureSafeOngoingAssessmentReplay = (replay: OngoingAssessmentReplay, selectedOrganizationId: string): boolean =>
  replay.IsSynthetic === true
  && replay.ReadOnly === true
  && replay.OrganizationId === selectedOrganizationId
  && ongoingAssessmentReplayFingerprint(replay) === expectedOngoingAssessmentReplayFingerprint();

const OngoingAssessmentReplayPage = () => {
  const { accessInfo } = useAccessInfo();
  const selectedOrganizationId = accessInfo?.selectedOrganizationId ?? '';
  const selectedOrganizationName = accessInfo?.selectedOrganizationName ?? '';
  const [replay, setReplay] = useState<OngoingAssessmentReplay | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setReplay(null);
    setFailed(false);
    new OngoingAssessmentReplayService().Get(selectedOrganizationId, selectedOrganizationName, controller.signal)
      .then(result => {
        if (!controller.signal.aborted && isCaptureSafeOngoingAssessmentReplay(result, selectedOrganizationId))
          setReplay(result);
        else if (!controller.signal.aborted)
          setFailed(true);
      })
      .catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [selectedOrganizationId, selectedOrganizationName]);

  if (failed)
    return <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="warning">Ongoing-assessment replay is unavailable for this organization.</Alert></Container>;
  if (!replay)
    return <Box role="status" aria-label="Loading ongoing-assessment replay" sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  return <OngoingAssessmentReplayPresentation replay={replay} />;
};

export const OngoingAssessmentReplayRoute = () => {
  const { accessInfo, hasOneOfRoles } = useAccessInfo();
  const authorized = hasOneOfRoles([Securables.organization_administrator, Securables.synergist_admin], true);
  const allowed = isNorthstarCaptureReplayOrganization(
    accessInfo?.selectedOrganizationId ?? '',
    accessInfo?.selectedOrganizationName ?? '',
    CAPTURE_REPLAY_ORGANIZATION_IDS,
  );
  return authorized && allowed ? <OngoingAssessmentReplayPage /> : <Redirect to="/" />;
};

export default OngoingAssessmentReplayPage;
